import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildWeeklySeries,
  formatWeekRange,
  getWeeklyMetrics,
} from '../utils/weeklyStatistics';
import './WeeklyStatistics.css';

function Metric({ label, value, detail, accent }) {
  return (
    <div className="weekly-stat__metric">
      <span className="weekly-stat__metric-label">{label}</span>
      <strong className={`weekly-stat__metric-value weekly-stat__metric-value--${accent || 'violet'}`}>
        {value}
      </strong>
      {detail && <span className="weekly-stat__metric-detail">{detail}</span>}
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  if (point.mood == null) return null;
  return (
    <div className="weekly-stat__tooltip">
      <strong>{point.day}, {point.shortDate}</strong>
      <span>{Math.round(point.mood)}% positive emotion share</span>
      <small>{point.sessions} {point.sessions === 1 ? 'session' : 'sessions'}</small>
    </div>
  );
}

function InsightPanel({ metrics, series }) {
  const recordedDays = series.filter((day) => day.sessions > 0).length;
  const highestDay = series.reduce((best, day) => (
    day.mood != null && (best == null || day.mood > best.mood) ? day : best
  ), null);

  return (
    <section className="weekly-stat__insight" aria-labelledby="weekly-insight-title">
      <div className="weekly-stat__eyebrow">WEEKLY INSIGHT</div>
      <h3 id="weekly-insight-title">A clearer read on your week</h3>
      <p>
        {metrics.totalSessions
          ? `${metrics.dominant.label} was your most observed emotion across ${metrics.totalSessions} ${metrics.totalSessions === 1 ? 'session' : 'sessions'}.`
          : 'Record a session to unlock a personalized weekly observation.'}
      </p>
      {highestDay && (
        <p className="weekly-stat__insight-note">
          Your strongest positive balance appeared on {highestDay.day}, {highestDay.shortDate}.
        </p>
      )}
      {!metrics.totalSessions && recordedDays === 0 && (
        <span className="weekly-stat__insight-muted">Insights are based only on your recorded sessions.</span>
      )}
    </section>
  );
}

export default function WeeklyStatistics({
  aggregate,
  previousAggregate,
  records = [],
  startDate,
  endDate,
}) {
  const metrics = useMemo(() => getWeeklyMetrics(aggregate, records), [aggregate, records]);
  const series = useMemo(
    () => buildWeeklySeries(records, startDate, endDate),
    [records, startDate, endDate],
  );
  const previous = useMemo(
    () => getWeeklyMetrics(previousAggregate, []),
    [previousAggregate],
  );
  const previousComparison = previousAggregate?.top_5
    ? metrics.dominant.key === previous.dominant.key
      ? 'Same dominant emotion as last week'
      : `Previously ${previous.dominant.label}`
    : null;
  const pieData = metrics.distribution.length ? metrics.distribution : [{ key: 'empty', value: 1, percentage: 100, color: 'var(--weekly-pie-empty)', label: 'No data' }];
  const hasTrend = series.some((day) => day.mood != null);
  const balanceLabel = metrics.positivePercentage == null
    ? 'No balance yet'
    : metrics.positivePercentage >= 55
      ? 'Leaning positive'
      : metrics.positivePercentage <= 45
        ? 'Leaning negative'
        : 'Well balanced';
  const mentalScore = metrics.positivePercentage == null ? null : Math.round(metrics.positivePercentage);

  return (
    <div className="weekly-statistics">
      <section className="weekly-stat__hero" aria-labelledby="weekly-stat-title">
        <div>
          <div className="weekly-stat__eyebrow">EMOTIONAL PULSE</div>
          <h2 id="weekly-stat-title">Your week in focus</h2>
          <p className="weekly-stat__range">{formatWeekRange(startDate, endDate)}</p>
        </div>
        <div className="weekly-stat__hero-emotion">
          <span className="weekly-stat__hero-emoji" role="img" aria-label={metrics.dominant.label}>
            {metrics.dominant.emoji}
          </span>
          <div>
            <span className="weekly-stat__hero-caption">Most felt</span>
            <strong>{metrics.dominant.label}</strong>
            <span>{metrics.dominant.percentage.toFixed(0)}% of your emotional mix</span>
          </div>
        </div>
        <div className="weekly-stat__hero-metrics">
          <Metric label="Sessions" value={metrics.totalSessions || '—'} detail="recorded this week" accent="cyan" />
          <Metric label="Balance" value={mentalScore == null ? '—' : `${mentalScore}%`} detail={balanceLabel} accent="violet" />
          {previousComparison && <Metric label="Week over week" value="Compared" detail={previousComparison} accent="gold" />}
        </div>
      </section>

      <div className="weekly-stat__grid">
        <section className="weekly-stat__card weekly-stat__card--trend" aria-labelledby="weekly-trend-title">
          <div className="weekly-stat__card-heading">
            <div>
              <span className="weekly-stat__eyebrow">MOOD TREND</span>
              <h3 id="weekly-trend-title">Positive emotion share</h3>
            </div>
            <span className="weekly-stat__card-meta">Daily average</span>
          </div>
          {hasTrend ? (
            <div className="weekly-stat__chart" role="img" aria-label="Daily positive emotion share across the week">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="weeklyMoodGradient" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="var(--weekly-chart-fill)" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="var(--weekly-chart-fade)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: 'var(--weekly-chart-axis)', fontSize: 12 }} />
                  <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: 'var(--weekly-chart-axis-muted)', fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'var(--weekly-chart-stroke)', strokeDasharray: '4 4' }} />
                  <Area type="monotone" dataKey="mood" stroke="var(--weekly-chart-fill)" strokeWidth={3} fill="url(#weeklyMoodGradient)" connectNulls={false} dot={{ r: 4, fill: 'var(--weekly-chart-dot)', stroke: 'var(--weekly-chart-dot-stroke)', strokeWidth: 2 }} activeDot={{ r: 6, fill: 'var(--weekly-chart-active-fill)', stroke: 'var(--weekly-chart-active-stroke)', strokeWidth: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="weekly-stat__empty-chart">
              <span>○</span>
              <p>No daily observations yet</p>
              <small>Your trend will appear after recording a session.</small>
            </div>
          )}
        </section>

        <section className="weekly-stat__card weekly-stat__card--distribution" aria-labelledby="weekly-distribution-title">
          <div className="weekly-stat__card-heading">
            <div>
              <span className="weekly-stat__eyebrow">EMOTIONAL DISTRIBUTION</span>
              <h3 id="weekly-distribution-title">Top emotions</h3>
            </div>
          </div>
          <div className="weekly-stat__donut-wrap">
            <ResponsiveContainer width="52%" height={190}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="label" innerRadius={55} outerRadius={78} paddingAngle={3} stroke="none">
                  {pieData.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]} contentStyle={{ background: 'var(--weekly-pie-tooltip-bg)', border: '1px solid var(--weekly-pie-tooltip-border)', borderRadius: 10, color: 'var(--weekly-text)' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="weekly-stat__donut-center">
              <strong>{metrics.dominant.percentage.toFixed(0)}%</strong>
              <span>{metrics.dominant.label}</span>
            </div>
            <div className="weekly-stat__legend">
              {metrics.distribution.map((emotion) => (
                <div className="weekly-stat__legend-row" key={emotion.key}>
                  <span><i style={{ backgroundColor: emotion.color }} />{emotion.label}</span>
                  <strong>{emotion.percentage.toFixed(0)}%</strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="weekly-stat__card weekly-stat__card--balance" aria-labelledby="weekly-balance-title">
          <div className="weekly-stat__card-heading">
            <div>
              <span className="weekly-stat__eyebrow">MOOD BALANCE</span>
              <h3 id="weekly-balance-title">{balanceLabel}</h3>
            </div>
          </div>
          <div className="weekly-stat__balance-bar" role="img" aria-label={`Positive ${metrics.positivePercentage == null ? 0 : Math.round(metrics.positivePercentage)} percent, negative ${metrics.negativePercentage == null ? 0 : Math.round(metrics.negativePercentage)} percent`}>
            <span style={{ width: `${metrics.positivePercentage || 0}%` }} />
          </div>
          <div className="weekly-stat__balance-labels">
            <span><b>{metrics.positivePercentage == null ? '—' : `${metrics.positivePercentage.toFixed(0)}%`}</b> positive</span>
            <span><b>{metrics.negativePercentage == null ? '—' : `${metrics.negativePercentage.toFixed(0)}%`}</b> negative</span>
          </div>
          <p className="weekly-stat__supporting-text">Based on the emotions detected in your recorded sessions.</p>
        </section>

        <section className="weekly-stat__card weekly-stat__card--state" aria-labelledby="weekly-state-title">
          <div className="weekly-stat__state-copy">
            <span className="weekly-stat__eyebrow">MENTAL STATE</span>
            <h3 id="weekly-state-title">{mentalScore == null ? 'Not enough data' : balanceLabel}</h3>
            <p>This is an observation of your recorded emotional mix, not a clinical assessment.</p>
          </div>
          <div className="weekly-stat__radial" style={{ '--weekly-score': `${mentalScore || 0}%` }} role="img" aria-label={mentalScore == null ? 'Mental state unavailable' : `${mentalScore}% positive emotional balance`}>
            <strong>{mentalScore == null ? '—' : mentalScore}</strong>
            <span>{mentalScore == null ? '' : 'balance'}</span>
          </div>
        </section>
      </div>
      <InsightPanel metrics={metrics} series={series} />
    </div>
  );
}
